import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { LoaderCircle, UserRound, Lock, BarChart3, Users, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { PageTransition } from "@/components/PageTransition";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import authService from "@/services/auth.service";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth.context";
import { CooperativeLogo } from "@/components/ui/cooperative-logo";
import { isAdmin } from "@/lib/utils";

import { Link, useLocation} from "react-router-dom";
import qs from "qs";

import { staggerContainer, staggerItem } from "@/lib/animations";

const formSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required("Username or email is required")
    .label("Username or email"),
  password: yup.string().required("Password is required").label("Passcord"),
});

export default function Login() {
  const location = useLocation();
  const [now, setNow] = useState(() => new Date());

  const form = useForm({
    resolver: yupResolver(formSchema),
    values: {
      email: "",
      password: "",
    },
  });

  const redirect = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.redirect;

  const defaultEmail = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.email;

  useEffect(() => {
    if (defaultEmail) {
      form.setValue("email", defaultEmail);
    }
  }, [defaultEmail]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000 * 30);
    return () => window.clearInterval(timer);
  }, []);

  const { user, setCurrentUser, loading } = useAuth();

  // Check if user is already logged in and needs to change password
  useEffect(() => {
    if (!loading) {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const token = localStorage.getItem("access_token");
      const requiresPasswordChange = user?.requiresPasswordChange || storedUser?.requiresPasswordChange;
      
      // If user is authenticated and needs to change password, redirect immediately
      if (token && requiresPasswordChange) {
        console.log("[Login] User already authenticated and needs password change, redirecting...");
        window.location.href = "/change-password?firstTime=true";
        return;
      }
      
      // If user is authenticated and doesn't need password change, redirect to dashboard
      if (token && user && !requiresPasswordChange) {
        console.log("[Login] User already authenticated, redirecting to dashboard...");
        const userIsAdmin = isAdmin(user);
        const defaultRedirect = userIsAdmin ? "/dashboard" : "/member/dashboard";
        window.location.href = (redirect as string) || defaultRedirect;
        return;
      }
    }
  }, [user, loading, redirect]);

  // const navigate = useNavigate();
  const [error, setError] = useState("");
  const dateLabel = now.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timeLabel = now.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const [timeValue, meridiem = ""] = timeLabel.split(" ");

  const submit = async (values: any) => {
    setError("");
    try {
      setError("");
      console.log("[Login] Attempting login for:", values.email);
      const res = await authService.signIn({
        email: values.email,
        password: values.password,
      });
      
      console.log("[Login] Login response:", res);
      console.log("[Login] requiresPasswordChange:", (res as any)?.requiresPasswordChange);
      console.log("[Login] User object:", JSON.stringify(res, null, 2));
      console.log("[Login] Leadership role:", (res as any)?.leadershipRole || (res as any)?.role?.name);
      console.log("[Login] IsAdmin:", (res as any)?.isAdmin);
      
      setCurrentUser(res);
      
      // Also refresh user data from server to ensure we have the latest role information
      try {
        const refreshedUser = await authService.getCurrentUser();
        if (refreshedUser) {
          console.log("[Login] Refreshed user data:", JSON.stringify(refreshedUser, null, 2));
          console.log("[Login] Refreshed leadership role:", (refreshedUser as any)?.leadershipRole || (refreshedUser as any)?.role?.name);
          setCurrentUser(refreshedUser);
        }
      } catch (error) {
        console.error("[Login] Error refreshing user data:", error);
      }
      
      // Check if password change is required (first-time login with default password)
      // Check both in response and localStorage (in case it was saved there)
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const requiresPasswordChange = (res as any)?.requiresPasswordChange || storedUser?.requiresPasswordChange;
      
      console.log("[Login] Final requiresPasswordChange check:", requiresPasswordChange);
      
      if (requiresPasswordChange) {
        console.log("[Login] Redirecting to change password page");
        window.location.href = "/change-password?firstTime=true";
        return;
      }
      
      // Redirect based on user role
      const finalUser = JSON.parse(localStorage.getItem("user") || "{}");
      const userIsAdmin = isAdmin(finalUser);
      const defaultRedirect = userIsAdmin ? "/dashboard" : "/member/dashboard";
      
      console.log("[Login] Final user check - isAdmin:", userIsAdmin, "role:", finalUser?.leadershipRole || finalUser?.role?.name);
      console.log("[Login] Redirecting to:", (redirect as string) || defaultRedirect);
      window.location.href = (redirect as string) || defaultRedirect;
    } catch (e: any) {
      console.error("[Login] Login error:", e);
      console.error("[Login] Error response status:", e?.response?.status);
      console.error("[Login] Error response data:", e?.response?.data);
      console.error("[Login] Error config URL:", e?.config?.url);
      console.error("[Login] Error config baseURL:", e?.config?.baseURL);
      console.error("[Login] Error config method:", e?.config?.method);
      console.error("[Login] Error request URL:", e?.request?.responseURL);
      const msg = e?.response?.data?.message || e?.message || "An error occured";
      setError(msg);
      const errors = e?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        form.setError(field, {
          message: errors[field],
        });
      });
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-[#fffffb] md:grid md:grid-cols-2">
        <section
          className="relative hidden md:flex md:flex-col"
          style={{
            background:
              "linear-gradient(135deg, #0A9F43, #12B24B)",
          }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div
              className="absolute -top-10 -left-10 h-72 w-72 opacity-[0.10]"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgba(255,255,255,0.7) 1.2px, transparent 1.2px)",
                backgroundSize: "24px 24px",
              }}
            />
            <div className="absolute -top-28 -right-28 h-[30rem] w-[30rem] rounded-full bg-white/[0.04]" />
            <div className="absolute -bottom-36 -left-20 h-[26rem] w-[26rem] rounded-full bg-white/[0.03]" />
            <div
              className="absolute bottom-8 right-8 h-48 w-48 opacity-[0.07]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, transparent, transparent 14px, rgba(255,255,255,0.6) 14px, rgba(255,255,255,0.6) 15px)",
              }}
            />
            {/* Extra organic blob for depth */}
            <div className="absolute top-1/3 -left-16 h-64 w-64 rounded-full bg-white/[0.03] blur-2xl" />
            <div className="absolute bottom-1/4 right-12 h-40 w-40 rounded-full bg-white/[0.04] blur-xl" />
          </div>

          {/* Top-left logo row */}
          <div className="relative z-10 flex-shrink-0 px-12 pt-10">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-white/25 bg-white/10">
                <CooperativeLogo size={40} circular />
              </span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                CAISSE D'ENTRAIDE IPRC
              </p>
            </div>
          </div>

          {/* Main area: text left, image right */}
          <div className="relative z-10 flex flex-1 items-center px-12">
            {/* Left text block — center-left */}
            <div className="w-[38%] pr-8">
              <h1 className="text-[2.4rem] font-bold leading-[1.15] tracking-tight text-white">
                Secure access for your<br />savings operations
              </h1>
              <p className="mt-5 text-base leading-7 text-white/80">
                Manage contributions, reports, and member workflows in one centralized
                workspace.
              </p>
              <div className="mt-10 space-y-4">
                {[
                  { Icon: BarChart3, text: "Real-time analytics and reporting" },
                  { Icon: Users, text: "Multi-group and member support" },
                  { Icon: ShieldCheck, text: "24/7 platform support" },
                ].map(({ Icon, text }) => (
                  <div key={text} className="flex items-center gap-4">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20">
                      <Icon className="h-3.5 w-3.5 text-white" />
                    </span>
                    <span className="text-[14px] text-white/90">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right illustration — ~62% width with blob clip */}
            <div className="w-[62%] flex items-center justify-center pl-4">
              <div className="relative w-full">
                <div className="absolute -top-6 -right-6 h-52 w-52 rounded-full bg-white/[0.05] blur-2xl" />
                <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-white/[0.03] blur-xl" />
                <div className="absolute top-1/3 -left-4 h-28 w-28 rounded-full bg-white/[0.04] blur-lg" />
                <img
                  src="/login-photo.png"
                  alt="Login illustration"
                  className="relative w-full object-cover shadow-2xl"
                  style={{
                    borderRadius: "55% 45% 50% 50% / 45% 50% 50% 55%",
                  }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    borderRadius: "55% 45% 50% 50% / 45% 50% 50% 55%",
                    background: "linear-gradient(135deg, rgba(10,159,67,0.18) 0%, transparent 55%)",
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="relative flex min-h-screen flex-col bg-[#F8FAFC]">
          <div className="pointer-events-none absolute inset-0 hidden overflow-hidden">
            {[
              "top-20 right-[-120px]",
              "top-64 right-[-70px]",
              "top-[28rem] right-[-150px]",
              "bottom-20 right-[-90px]",
            ].map((pos, idx) => (
              <span
                key={idx}
                className={`absolute ${pos} text-[28px] font-semibold tracking-[0.16em] text-[#050500]/[0.08] whitespace-nowrap rotate-[22deg]`}
              >
                INFINITY TECH SOLUTIONS
              </span>
            ))}
          </div>
          <motion.div
            className="pointer-events-none absolute inset-0 hidden opacity-55"
            style={{
              backgroundImage:
                "radial-gradient(circle at 10% 20%, rgba(255,255,255,0.9) 2px, transparent 3px), radial-gradient(circle at 72% 30%, rgba(255,255,255,0.8) 2px, transparent 3px), radial-gradient(circle at 80% 75%, rgba(255,255,255,0.85) 2px, transparent 3px)",
            }}
            animate={{ opacity: [0.45, 0.62, 0.45] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="relative z-10 flex flex-1 items-center justify-center px-8 py-10">
            <motion.div
              layout
              className="w-full max-w-[520px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 250, damping: 24 }}
            >
              <div className="md:hidden mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A9F43]/5 to-[#12B24B]/10">
                <img
                  src="/login-photo.png"
                  alt="Login illustration"
                  className="h-48 w-full object-contain p-4"
                />
              </div>
              <Card className="w-full rounded-none border-0 bg-transparent shadow-none">
                <CardHeader className="px-0 pb-0 pt-0 text-center">
                  <div className="mb-14">
                    <p className="text-[15px] font-semibold text-[#0A9F43]">
                      {dateLabel}
                    </p>
                    <div className="mt-2 flex items-end justify-center gap-2 text-[#566579]">
                      <span className="text-[52px] font-bold leading-none tracking-normal">
                        {timeValue}
                      </span>
                      <span className="mb-1 text-[15px] font-medium uppercase">
                        {meridiem}
                      </span>
                    </div>
                  </div>

                  <h1 className="text-[16px] font-medium text-[#22385a]">
                    Enter your email and password
                  </h1>
                </CardHeader>

                <CardContent className="space-y-5 px-0 pb-0 pt-8">
                  <AnimatePresence mode="wait">
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 14 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Alert variant="danger">{error}</Alert>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={form.handleSubmit(submit)}>
                    <motion.div
                      variants={staggerContainer}
                      initial="hidden"
                      animate="show"
                      className="space-y-5"
                    >
                      <motion.div variants={staggerItem}>
                        <Label htmlFor="email" className="sr-only">
                          Username
                        </Label>
                        <div className="relative">
                          <UserRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#61728a]" />
                          <Input
                            id="email"
                            placeholder="Enter Your Username"
                            value={form.watch("email")}
                            onChange={(e) => form.setValue("email", e.target.value)}
                            error={form.formState?.errors["email"]?.message}
                            name="email"
                            className="h-[58px] w-full border-[#cfd6df] bg-white pl-12 pr-4 text-[15px] font-medium text-[#22385a] shadow-none placeholder:text-[#526b8c] focus-visible:border-[#0A9F43] focus-visible:ring-1 focus-visible:ring-[#0A9F43]"
                          />
                        </div>
                      </motion.div>

                      <motion.div variants={staggerItem}>
                        <Label htmlFor="password" className="sr-only">
                          Password
                        </Label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#61728a]" />
                          <Input
                            id="password"
                            placeholder="Enter Your Password"
                            value={form.watch("password")}
                            onChange={(e) => form.setValue("password", e.target.value)}
                            type="password"
                            error={form.formState?.errors["password"]?.message}
                            name="password"
                            className="h-[58px] w-full border-[#cfd6df] bg-white pl-12 pr-4 text-[15px] font-medium text-[#22385a] shadow-none placeholder:text-[#526b8c] focus-visible:border-[#0A9F43] focus-visible:ring-1 focus-visible:ring-[#0A9F43]"
                          />
                        </div>
                      </motion.div>

                      <motion.div variants={staggerItem} className="pt-2">
                        <Button
                          disabled={form.formState.isSubmitting}
                          onClick={form.handleSubmit(submit)}
                          className="h-[60px] w-full bg-[#0A9F43] text-[16px] font-bold text-white shadow-none hover:bg-[#0A8F3A]"
                          type="submit"
                        >
                          {form.formState.isSubmitting ? (
                            <span className="flex items-center gap-2">
                              <LoaderCircle className="h-4 w-4 animate-spin" />
                              Signing in...
                            </span>
                          ) : (
                            "Login"
                          )}
                        </Button>
                      </motion.div>

                      <motion.p
                        variants={staggerItem}
                        className="pt-2 text-center text-[13px]"
                      >
                        <Link className="font-medium text-[#0A9F43] hover:underline" to="/forgot-password">
                          Forgot Password?
                        </Link>
                      </motion.p>
                    </motion.div>
                  </form>
                </CardContent>

                <CardFooter className="border-0 px-0 pb-0 pt-4">
                  <p className="mx-auto max-w-[460px] text-center text-[13px] leading-6 text-[#52627a]">
                    By continuing, you agree to our{" "}
                    <Link to="/terms" className="underline underline-offset-2 hover:text-[#0A9F43]">
                      Terms of Service
                    </Link>
                    {" "}and{" "}
                    <Link to="/privacy" className="underline underline-offset-2 hover:text-[#0A9F43]">
                      Privacy Policy
                    </Link>.
                  </p>
                </CardFooter>
              </Card>
            </motion.div>
          </div>

          <footer className="relative z-10 hidden border-t border-[#050500]/10 px-6 py-3 text-[11px] text-[#050500]/70">
            <div className="mx-auto flex w-full max-w-3xl items-center justify-between">
              <span>© {new Date().getFullYear()} Infinity Tech Solutions. All rights reserved.</span>
              <div className="flex items-center gap-4">
                <Link to="/terms" className="hover:text-[#0A9F43]">Terms</Link>
                <Link to="/privacy" className="hover:text-[#0A9F43]">Privacy</Link>
                <Link to="/contact" className="hover:text-[#0A9F43]">Help</Link>
              </div>
            </div>
          </footer>
        </section>
      </div>
    </PageTransition>
  );
}
